import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/**
 * Per-traveler, type-aware detail fields rendered inside the expanded
 * traveler block of AddSegmentDialog and EditSegmentDialog.
 *
 * Renders (conditional on segmentType):
 *   - Confirmation Number (all types)
 *   - Train: Car + Seat + Class
 *   - Flight/Bus/Cruise: Seat/Cabin + Meal Preference
 *   - Accommodation: Room Info + Special Requests
 *   - Activity: Ticket Number
 *   - Car rental / Rideshare: Driver License
 *   - Notes (all types)
 *
 * Props:
 *   traveler            — full traveler object (id used for input ids only)
 *   travelerDetail      — existing per-traveler detail row (may be empty obj)
 *   segmentType         — 'flight' | 'train' | 'bus' | 'cruise' | ...
 *   updateTravelerDetail— (travelerId, field, value) => void
 */
export const TravelerTypeFields = ({
  traveler,
  travelerDetail,
  segmentType,
  updateTravelerDetail,
}) => {
  const detail = travelerDetail || {};
  const set = (field, value) => updateTravelerDetail(traveler.id, field, value);

  return (
    <>
      {/* Confirmation Number - all types */}
      <div className="grid gap-2">
        <Label htmlFor={`conf-${traveler.id}`} className="text-xs">Confirmation Number</Label>
        <Input
          id={`conf-${traveler.id}`}
          placeholder="e.g., ABC123"
          value={detail.confirmation_number || ""}
          onChange={(e) => set('confirmation_number', e.target.value)}
          className="h-8 text-sm"
        />
      </div>

      {/* Train: Car + Seat + Class (Meal Pref is irrelevant for rail) */}
      {segmentType === 'train' && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-2">
              <Label htmlFor={`car-${traveler.id}`} className="text-xs">Car/Coach Number</Label>
              <Input
                id={`car-${traveler.id}`}
                placeholder="e.g., 5"
                value={detail.car_number || ""}
                onChange={(e) => set('car_number', e.target.value)}
                className="h-8 text-sm"
                data-testid={`train-car-number-${traveler.id}`}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`seat-${traveler.id}`} className="text-xs">Seat Number</Label>
              <Input
                id={`seat-${traveler.id}`}
                placeholder="e.g., 12A"
                value={detail.seat_number || ""}
                onChange={(e) => set('seat_number', e.target.value)}
                className="h-8 text-sm"
                data-testid={`train-seat-number-${traveler.id}`}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`class-${traveler.id}`} className="text-xs">Class</Label>
            <Select
              value={detail.ticket_class || ""}
              onValueChange={(value) => set('ticket_class', value === "none" ? "" : value)}
            >
              <SelectTrigger
                id={`class-${traveler.id}`}
                className="h-8 text-sm"
                data-testid={`train-class-${traveler.id}`}
              >
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="Standard">Standard / 2nd Class</SelectItem>
                <SelectItem value="First">First Class</SelectItem>
                <SelectItem value="Business">Business</SelectItem>
                <SelectItem value="Premium">Premium</SelectItem>
                <SelectItem value="Sleeper">Sleeper</SelectItem>
                <SelectItem value="Couchette">Couchette</SelectItem>
                <SelectItem value="Economy">2nd Class</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Flight / Bus / Cruise: Seat (or Cabin) + Meal Preference */}
      {['flight', 'bus', 'cruise'].includes(segmentType) && (
        <>
          <div className="grid gap-2">
            <Label htmlFor={`seat-${traveler.id}`} className="text-xs">
              {segmentType === 'cruise' ? 'Cabin Number' : 'Seat Number'}
            </Label>
            <Input
              id={`seat-${traveler.id}`}
              placeholder={segmentType === 'cruise' ? 'e.g., D420' : 'e.g., 12A'}
              value={detail.seat_number || ""}
              onChange={(e) => set('seat_number', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          {segmentType === 'flight' && (
            <div className="grid gap-2">
              <Label htmlFor={`class-${traveler.id}`} className="text-xs">Class</Label>
              <Select
                value={detail.ticket_class || ""}
                onValueChange={(value) => set('ticket_class', value === "none" ? "" : value)}
              >
                <SelectTrigger
                  id={`class-${traveler.id}`}
                  className="h-8 text-sm"
                  data-testid={`flight-class-${traveler.id}`}
                >
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="Economy">Economy</SelectItem>
                  <SelectItem value="Premium Economy">Premium Economy</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="First">First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor={`meal-${traveler.id}`} className="text-xs">Meal Preference</Label>
            <Select
              value={detail.meal_preference || ""}
              onValueChange={(value) => set('meal_preference', value)}
            >
              <SelectTrigger id={`meal-${traveler.id}`} className="h-8 text-sm">
                <SelectValue placeholder="Select meal preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="vegetarian">Vegetarian</SelectItem>
                <SelectItem value="vegan">Vegan</SelectItem>
                <SelectItem value="gluten_free">Gluten Free</SelectItem>
                <SelectItem value="kosher">Kosher</SelectItem>
                <SelectItem value="halal">Halal</SelectItem>
                <SelectItem value="child_meal">Child Meal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Accommodation: Room + Special Requests */}
      {segmentType === 'accommodation' && (
        <>
          <div className="grid gap-2">
            <Label htmlFor={`room-${traveler.id}`} className="text-xs">Room Type / Number</Label>
            <Input
              id={`room-${traveler.id}`}
              placeholder="e.g., King Suite, Room 405"
              value={detail.room_info || ""}
              onChange={(e) => set('room_info', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`requests-${traveler.id}`} className="text-xs">Special Requests</Label>
            <Input
              id={`requests-${traveler.id}`}
              placeholder="e.g., High floor, away from elevator"
              value={detail.special_requests || ""}
              onChange={(e) => set('special_requests', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </>
      )}

      {/* Activity: Ticket Number */}
      {segmentType === 'activity' && (
        <div className="grid gap-2">
          <Label htmlFor={`ticket-${traveler.id}`} className="text-xs">Ticket / Pass Number</Label>
          <Input
            id={`ticket-${traveler.id}`}
            placeholder="e.g., TKT-12345"
            value={detail.ticket_number || ""}
            onChange={(e) => set('ticket_number', e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      )}

      {/* Car rental / Rideshare: Driver License */}
      {['car_rental', 'rideshare'].includes(segmentType) && (
        <div className="grid gap-2">
          <Label htmlFor={`license-${traveler.id}`} className="text-xs">Driver License #</Label>
          <Input
            id={`license-${traveler.id}`}
            placeholder="e.g., D12345678"
            value={detail.license_number || ""}
            onChange={(e) => set('license_number', e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      )}

      {/* Notes - all types */}
      <div className="grid gap-2">
        <Label htmlFor={`notes-${traveler.id}`} className="text-xs">Notes</Label>
        <Textarea
          id={`notes-${traveler.id}`}
          placeholder="Additional notes for this traveler..."
          value={detail.notes || ""}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className="text-sm"
        />
      </div>
    </>
  );
};

export default TravelerTypeFields;
